import { translations } from '@internal/i18n/translations'
import { getName } from '@internal/services/TSalesManager'
import { SalesManagerContext } from '@internal/utils/SalesManager/SalesManagerContext'
import { useFeatureFlag } from '@internal/utils/featureFlags/useFeatureFlag'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { Typography } from '@mui/material'
import { Text } from '@renderer-ui-library/atoms'
import {
  ActionableButton,
  ProfilePictureMedia,
  TButtonAction,
  TButtonData,
} from '@renderer-ui-library/molecules'
import { Media } from '@renderer-ui-library/molecules/Media/Media'
import { BlockWrapper } from '@renderer-ui-library/scaffolding/BlockWrapper/BlockWrapper'
import { BlockProps } from 'blocks/BlockProps'
import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, { useContext, useMemo } from 'react'
import { ISalesManagerBlockData } from './ISalesManagerBlockData'
import styles from './salesManagerBlock.module.scss'

export const SalesManagerBlock: React.FC<BlockProps<ISalesManagerBlockData>> =
  React.memo((props) => {
    const { t } = useTranslation(localeNamespace.common)
    const { hasFeature } = useFeatureFlag()
    const isZohoUsersFeatureEnabled = hasFeature('zohoUsers')
    const { salesManagers } = useContext(SalesManagerContext)

    const salesManager = useMemo(() => {
      if (isZohoUsersFeatureEnabled) {
        return salesManagers.find((m) =>
          m.assignments?.some(
            (a) => a.pageKey === props.data.zohoUserAssignmentKey
          )
        )
      }

      return props.data.salesManager.data?.attributes
    }, [
      isZohoUsersFeatureEnabled,
      props.data.salesManager.data?.attributes,
      props.data.zohoUserAssignmentKey,
      salesManagers,
    ])

    const { media, button } = useMemo(() => {
      if (!salesManager) {
        return { media: null, button: null }
      }

      const media: ProfilePictureMedia = {
        id: salesManager.id,
        type: 'profile-picture',
        name: getName(salesManager),
        variant: props.data.image === 'indoorImage' ? 'indoors' : 'outdoors',
        zohoUserId: salesManager.zohoUserId,
        transformations: {
          width: 800,
          height: 600,
        },
      }

      const button: TButtonData = {
        id: 'contact',
        text: t(translations.contactMeNow),
        url: '',
        systemPage: { data: null },
        page: { data: null },
        machineCategoryPage: { data: null },
        action: TButtonAction.openContactUsForm,
      }
      return { media, button }
    }, [salesManager, t, props.data.image])

    if (!salesManager || !media) {
      return null
    }

    const name = `${salesManager.firstName} ${salesManager.lastName}`

    return (
      <BlockWrapper blockData={props.data}>
        <div
          className={classnames(styles.wrapper, {
            [styles.imageRight]: props.data.orientation === 'imageRight',
          })}
        >
          <div className={styles.mediaWrapper}>
            <div
              className={classnames(styles.media, {
                [styles.highlight]: props.data.highlight,
              })}
            >
              <Media media={media} ratio={4 / 3} />
            </div>
          </div>
          <div className={styles.contentWrapper}>
            <div>
              <div className={styles.textContainer}>
                <Text
                  text={props.data.text}
                  highlight={props.data.highlight}
                  color={props.data.highlight ? 'bright' : 'dark'}
                />
              </div>
              <div
                className={classnames(styles.textContainer, {
                  [styles.highlight]: props.data.highlight,
                })}
              >
                <Typography variant='h4' marginBottom={0}>
                  {name}
                </Typography>
                <Typography variant='body1'>{salesManager.jobTitle}</Typography>
              </div>
              <ActionableButton
                button={button}
                color='secondary'
                variant='outlined'
              />
            </div>
          </div>
        </div>
      </BlockWrapper>
    )
  })

SalesManagerBlock.displayName = 'SalesManagerBlock'
